import { useQuery } from "@apollo/client/react";
import {
  CurrencyCode,
  toCurrencyCode,
} from "@repo/components/src/utils/money/currencies";
import {
  WorkspaceContextDocument,
  WorkspaceContextQuery,
} from "@repo/graphql/documents/workspace/query.generated";
import { createContext, ReactNode, useContext, useMemo } from "react";

type UserWorkspace = NonNullable<
  WorkspaceContextQuery["workspace"]["myWorkspace"]
>;

type WorkspaceContextType = {
  loading: boolean;
  userWorkspace?: UserWorkspace;
  workspace?: UserWorkspace["workspace"];
  workspaceId?: string;
  accountHolder: boolean;
  billingCountry?: UserWorkspace["workspace"]["billingCountry"];
  billingCurrencyCode: CurrencyCode | null;
  paymentProviderCode?: string | null;
  taxProfile?: UserWorkspace["workspace"]["taxProfile"];
};

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(
  undefined,
);

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const { data, loading } = useQuery(WorkspaceContextDocument);
  const userWorkspace = data?.workspace.myWorkspace ?? undefined;
  const workspace = userWorkspace?.workspace;

  const value = useMemo<WorkspaceContextType>(
    () => ({
      loading,
      userWorkspace,
      workspace,
      workspaceId: workspace?.id,
      accountHolder: userWorkspace?.accountHolder ?? false,
      billingCountry: workspace?.billingCountry,
      billingCurrencyCode: toCurrencyCode(workspace?.billingCurrencyCode),
      paymentProviderCode: workspace?.paymentProviderCode,
      taxProfile: workspace?.taxProfile,
    }),
    [loading, userWorkspace, workspace],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error(
      "useWorkspaceContext must be used within a WorkspaceProvider",
    );
  }
  return context;
};
